// ErrorNotice — inline chat callout for ephemeral errors / info notices.
//
// Design goals (2026-04-21):
//   • One surface. Errors only appear here, never in the status bar.
//   • Chat-aligned. Same font as the surrounding messages; NOT monospace.
//   • Friendly on top. Technical details (code, provider, model, raw msg)
//     live under a "Details" disclosure, useful for devs without bullying
//     the user with error codes.
//   • Retry is implicit: the WS hook already restores the user's last
//     input into the textarea on any error, so the action the callout
//     surfaces is "Dismiss".

import { useState } from 'react';
import type { EphemeralNotice } from '../store';
import { useActiveWorldStore } from '../store';

interface ErrorNoticeProps {
  notice: EphemeralNotice;
  /** CSS px string from FONT_SIZES (e.g. '15px') so notices match chat size. */
  fontSize: string;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function ErrorNotice({ notice, fontSize }: ErrorNoticeProps) {
  const [expanded, setExpanded] = useState(false);
  const removeEphemeralNotice = useActiveWorldStore((s) => s.removeEphemeralNotice);

  const isError = notice.kind === 'error';
  const details = notice.errorDetails;

  // Tokens: error vs info. Left-border accent + tinted background, NOT a
  // loud red block. Muted enough to sit next to assistant messages without
  // stealing attention from the story.
  const accent = isError
    ? 'border-l-red-400/70 bg-red-400/[0.06]'
    : 'border-l-amber-300/70 bg-amber-300/[0.05]';
  const iconColor = isError ? 'text-red-400' : 'text-amber-300';

  return (
    <div className="flex justify-start group">
      <div
        className={`max-w-[95%] md:max-w-[80%] rounded-r-md border-l-2 pl-3 pr-3 py-2 ${accent}`}
        role={isError ? 'alert' : 'status'}
      >
        {/* Primary line: icon + friendly message + inline timestamp */}
        <div className="flex items-start gap-2">
          <span className={`${iconColor} flex-shrink-0 mt-0.5`} aria-hidden>
            {isError ? '⚠' : 'ℹ'}
          </span>
          <div className="flex-1 min-w-0">
            <p
              className="text-dark-50 leading-snug"
              style={{ fontSize }}
            >
              {notice.title}
            </p>
            <div className="mt-1 flex items-center gap-3 text-xs text-dark-300">
              <span>{formatTimestamp(notice.created_at)}</span>
              {details && (
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="hover:text-dark-100 transition-colors"
                  aria-expanded={expanded}
                >
                  {expanded ? 'Hide details' : 'Details'}
                </button>
              )}
              <button
                type="button"
                onClick={() => removeEphemeralNotice(notice.id)}
                className="hover:text-dark-100 transition-colors"
                title="Dismiss"
              >
                Dismiss
              </button>
            </div>

            {/* Details disclosure — devs only. Monospace here is fine; it's
                opt-in and these fields are literal identifiers. */}
            {expanded && details && (
              <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs font-mono text-dark-300">
                <dt className="text-dark-400">code</dt>
                <dd className="break-all">{details.code}</dd>

                {details.phase && (
                  <>
                    <dt className="text-dark-400">phase</dt>
                    <dd className="break-all">{details.phase}</dd>
                  </>
                )}

                {details.provider && (
                  <>
                    <dt className="text-dark-400">provider</dt>
                    <dd className="break-all">{details.provider}</dd>
                  </>
                )}

                {details.model && (
                  <>
                    <dt className="text-dark-400">model</dt>
                    <dd className="break-all">{details.model}</dd>
                  </>
                )}

                <dt className="text-dark-400">detail</dt>
                <dd className="break-all whitespace-pre-wrap">{details.rawMessage}</dd>
              </dl>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
